import { useEffect, type ReactElement } from "react";
import { toast, type ToastItem } from "react-toastify";
import { ANNOUNCER_REGION_IDS, announce } from "../lib/announce";

export const ERROR_TOAST_AUTO_CLOSE_MS = 8000;

const toastText = (content: ToastItem["content"]): string =>
  typeof content === "string" || typeof content === "number" ? String(content) : "";

export function handleToastChange(item: ToastItem): void {
  if (item.status !== "added") return;
  const isUrgent = item.type === "error" || item.type === "warning";
  // Errors stay up longer so sighted users can read them; the announcement
  // below means screen reader users don't depend on the toast's timing at all.
  if (item.type === "error") {
    toast.update(item.id, { autoClose: ERROR_TOAST_AUTO_CLOSE_MS });
  }
  announce(toastText(item.content), isUrgent ? "assertive" : "polite");
}

/**
 * Mounts the app-level live regions used by `announce` and voices every toast
 * through them. The regions exist from first paint, which JAWS needs to pick
 * up later changes; toasts render their own container late and are often missed.
 */
export default function LiveAnnouncer(): ReactElement {
  useEffect(() => toast.onChange(handleToastChange), []);

  return (
    <>
      <div
        id={ANNOUNCER_REGION_IDS.polite}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      />
      <div
        id={ANNOUNCER_REGION_IDS.assertive}
        className="sr-only"
        aria-live="assertive"
        aria-atomic="true"
      />
    </>
  );
}
